import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Pill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PrefTable from "@/components/PrefTable";
import { FileText, Loader2, Plus, ArrowRight } from "lucide-react";

const STATUS_COLOUR = {
  Draft: "slate", Sent: "blue", Accepted: "green", Declined: "red", Expired: "amber",
};

const gbp = (v) => `£${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function QuotesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [taskId, setTaskId] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/quotes").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const [params] = useSearchParams();
  useEffect(() => { const t = params.get("task"); if (t) { setTaskId(t); setNewOpen(true); } else if (params.get("new")) setNewOpen(true); }, [params]);

  const createQuote = async () => {
    setCreating(true);
    try {
      const r = await api.post("/quotes", {
        task_legacy_id: taskId.trim() || null,
        status: "Draft",
        lines: [],
      });
      toast.success(`Created ${r.data.quote_number}`);
      setNewOpen(false);
      setTaskId("");
      navigate(`/quotes/${r.data.quote_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create quote");
    } finally {
      setCreating(false);
    }
  };

  const QUOTE_COLUMNS = [
    { key: "quote_number", label: "Quote", mandatory: true, defaultWidth: 150, strong: true,
      render: (v) => <span className="font-mono font-semibold text-indigo-700">{v}</span> },
    { key: "customer_name", label: "Customer", truncate: true, render: (v) => v || "—" },
    { key: "task_legacy_id", label: "Linked Job", mono: true,
      render: (v) => (v ? <Link to={`/tasks/${v}`} onClick={(e) => e.stopPropagation()} className="text-indigo-600 hover:underline font-mono">{v}</Link> : "—") },
    { key: "quote_date", label: "Date" },
    { key: "status", label: "Status", render: (v) => <Pill colour={STATUS_COLOUR[v] || "slate"}>{v}</Pill> },
    { key: "total_inc_vat", label: "Total (inc VAT)", align: "right", strong: true, render: (v, q) => gbp(q.totals?.total_inc_vat) },
  ];

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="quotes-page">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <PageIcon />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Quotes</h1>
            <p className="text-sm text-slate-500 mt-0.5">Demonstration quotes linked to imported jobs · fixed HTSS layout.</p>
          </div>
        </div>
        <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setNewOpen(true)} data-testid="new-quote-btn">
          <Plus className="w-4 h-4" /> New Quote
        </Button>
      </div>

      {!items ? (
        <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading quotes…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-sm text-slate-400 py-16 bg-white rounded-2xl border border-dashed border-slate-200">No quotes yet. Create one from a job.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-3">
          <PrefTable tableId="quotes" columns={QUOTE_COLUMNS} rows={items} rowKey={(q) => q.quote_number}
            onRowClick={(q) => navigate(`/quotes/${q.quote_number}`)} rowTestId={(q) => `quote-row-${q.quote_number}`} testid="quotes-table-wrap" />
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-quote-dialog">
          <DialogHeader><DialogTitle>New demonstration quote</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Link to task (legacy ID, optional)</label>
              <Input value={taskId} onChange={(e) => setTaskId(e.target.value)} placeholder="e.g. 10001" className="mt-1" data-testid="new-quote-task-input" />
              <p className="text-xs text-slate-400 mt-1">Customer, venue and depot are auto-filled from the job.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={createQuote} disabled={creating} data-testid="new-quote-create">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
